import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import refreshToken from "../../../utils/refreshToken";
import api from "../../../services/api";
import {
  fetchAgendaDay,
  fetchAgendaWeekSummary,
} from "../../../services/scheduleAgenda";
import { useAuth } from "../../../hooks/auth";
import { useToast } from "../../../hooks/toast";
import { useRefresh } from "../../../hooks/refresh";
import { useScheduleTeam } from "../../../hooks/scheduleTeam";
import {
  AgendaDayPayload,
  AgendaFiltersState,
  AgendaWeekSummary,
} from "../agendaTypes";
import {
  buildAgendaSearch,
  getFortalezaTodayIso,
  hasExplicitAgendaDateInSearch,
  mergeAgendaFilters,
  parseAgendaFiltersFromSearch,
} from "../agendaFiltersQuery";
import { clampAgendaScheduleDate } from "../agendaWeekDateRange";
import {
  adjacentAgendaDates,
  buildAgendaDayCacheKey,
  buildAgendaWeekCacheKey,
  clearAgendaCache,
  getCachedAgendaDay,
  getCachedAgendaWeek,
  hasCachedAgendaDay,
  setCachedAgendaDay,
  setCachedAgendaWeek,
} from "../agendaDayCache";

type UseAgendaPageDataResult = {
  filters: AgendaFiltersState;
  applyFilters: (patch: Partial<AgendaFiltersState>) => void;
  dayData: AgendaDayPayload | null;
  weekSummary: AgendaWeekSummary | null;
  loadingInitial: boolean;
  loadingDay: boolean;
  errorMessage: string;
  retry: () => void;
  reloadToken: number;
};

const AGENDA_FILTERS_STORAGE_PREFIX = "@7Cantos:agenda-filters:";

/** Day, week and prefetch requests share one token check (refreshToken also dedupes). */
async function authorizeAgendaRequests(dateToExpires: Date, accessToken: string) {
  const token = await refreshToken(dateToExpires, accessToken);
  api.defaults.headers.authorization = `Bearer ${token ?? accessToken}`;
}

function saveAgendaFilters(userId: number | null | undefined, filters: Partial<AgendaFiltersState>) {
  if (userId == null) {
    return;
  }
  localStorage.setItem(
    `${AGENDA_FILTERS_STORAGE_PREFIX}${userId}`,
    JSON.stringify({
      city: filters.city,
      consultantId: filters.consultantId,
      visitType: filters.visitType,
      teamId: filters.teamId,
    })
  );
}

function readSavedAgendaFilters(userId?: number | null): Partial<AgendaFiltersState> {
  if (userId == null) {
    return {};
  }

  try {
    const raw = localStorage.getItem(`${AGENDA_FILTERS_STORAGE_PREFIX}${userId}`);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as Partial<AgendaFiltersState>;
    return {
      city: typeof parsed.city === "string" && parsed.city.trim() ? parsed.city : undefined,
      consultantId:
        typeof parsed.consultantId === "number" && parsed.consultantId > 0
          ? parsed.consultantId
          : undefined,
      visitType:
        parsed.visitType === "presential" || parsed.visitType === "video"
          ? parsed.visitType
          : "all",
      teamId:
        typeof parsed.teamId === "number" && parsed.teamId > 0
          ? parsed.teamId
          : undefined,
    };
  } catch {
    return {};
  }
}

export function useAgendaPageData(): UseAgendaPageDataResult {
  const history = useHistory();
  const location = useLocation();
  const { access_token, dateToExpires, user } = useAuth();
  const { addToast } = useToast();
  const { refresh } = useRefresh();
  const {
    selectedTeamId,
    teamsFetchDone,
    usesTeamSchedules,
  } = useScheduleTeam();

  const [dayData, setDayData] = useState<AgendaDayPayload | null>(null);
  const [weekSummary, setWeekSummary] = useState<AgendaWeekSummary | null>(null);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingDay, setLoadingDay] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  const savedAgendaFilters = useMemo(
    () => readSavedAgendaFilters(user?.id),
    [user?.id]
  );

  const parsedFilters = useMemo(
    () =>
      parseAgendaFiltersFromSearch(location.search, {
        date: getFortalezaTodayIso(),
        ...savedAgendaFilters,
      }),
    [location.search, savedAgendaFilters]
  );

  useEffect(() => {
    if (location.pathname !== "/schedules") {
      return;
    }

    if (hasExplicitAgendaDateInSearch(location.search)) {
      return;
    }

    const nextFilters = parseAgendaFiltersFromSearch(location.search, {
      date: getFortalezaTodayIso(),
      ...savedAgendaFilters,
    });
    const search = buildAgendaSearch(nextFilters);
    history.replace(`/schedules${search}`);
  }, [history, location.pathname, location.search, savedAgendaFilters]);

  const filters = useMemo<AgendaFiltersState>(() => {
    const teamId =
      usesTeamSchedules && selectedTeamId != null
        ? selectedTeamId
        : parsedFilters.teamId;
    const todayIso = getFortalezaTodayIso();

    return {
      ...parsedFilters,
      teamId,
      date: clampAgendaScheduleDate(parsedFilters.date, todayIso),
    };
  }, [parsedFilters, usesTeamSchedules, selectedTeamId]);

  useEffect(() => {
    if (user?.id == null) {
      return;
    }

    saveAgendaFilters(user.id, filters);
  }, [filters.city, filters.consultantId, filters.teamId, filters.visitType, user?.id]);

  useEffect(() => {
    if (location.pathname !== "/schedules") {
      return;
    }

    const todayIso = getFortalezaTodayIso();
    const clampedDate = clampAgendaScheduleDate(filters.date, todayIso);
    if (clampedDate === filters.date) {
      return;
    }

    const search = buildAgendaSearch({ ...filters, date: clampedDate });
    history.replace(`/schedules${search}`);
  }, [filters, history, location.pathname]);

  const applyFilters = useCallback(
    (patch: Partial<AgendaFiltersState>) => {
      const todayIso = getFortalezaTodayIso();
      const merged = mergeAgendaFilters(filters, patch);
      const next = {
        ...merged,
        date: clampAgendaScheduleDate(merged.date, todayIso),
      };
      // Persist synchronously before changing the URL. Otherwise clearing a
      // filter can be immediately overwritten by the previous saved value
      // while the location change is being parsed.
      saveAgendaFilters(user?.id, next);
      const search = buildAgendaSearch(next);
      const target = `/schedules${search}`;
      const current = `${location.pathname}${location.search}`;
      if (current !== target) {
        history.replace(target);
      }
    },
    [filters, history, location.pathname, location.search, user?.id]
  );

  const retry = useCallback(() => {
    setReloadToken((value) => value + 1);
  }, []);

  // Bumped by any change on the agenda (lock, booking, outcome, retry): cached days are stale.
  const revision = `${refresh}|${reloadToken}`;
  const revisionRef = useRef(revision);
  const lastLoadedKeyRef = useRef<string | null>(null);
  const weekBaseKeyRef = useRef<string | null>(null);
  const dayDataRef = useRef<AgendaDayPayload | null>(null);
  dayDataRef.current = dayData;

  const canLoad = Boolean(user) && (!usesTeamSchedules || teamsFetchDone);

  // Day grid: shown as soon as it arrives, without waiting for the week strip.
  useEffect(() => {
    if (!user || !canLoad) {
      return;
    }

    let cancelled = false;
    const userId = Number(user.id);
    const key = buildAgendaDayCacheKey(userId, filters);

    if (revisionRef.current !== revision) {
      revisionRef.current = revision;
      clearAgendaCache();
    }

    const cached = getCachedAgendaDay(key);
    if (cached) {
      setDayData(cached);
      setErrorMessage("");
    }

    // Same view reloaded (after an action) or a day already seen: refresh quietly.
    const isBackground =
      Boolean(cached) || (key === lastLoadedKeyRef.current && dayDataRef.current !== null);

    if (isBackground) {
      // A previous load may have been interrupted while showing the skeleton.
      setLoadingInitial(false);
      setLoadingDay(false);
    } else if (dayDataRef.current === null) {
      setLoadingInitial(true);
    } else {
      setLoadingDay(true);
    }

    (async () => {
      try {
        await authorizeAgendaRequests(dateToExpires, access_token);
        const day = await fetchAgendaDay(filters);
        setCachedAgendaDay(key, day);

        if (cancelled) {
          return;
        }

        lastLoadedKeyRef.current = key;
        setDayData(day);
        setErrorMessage("");
        void prefetchAdjacentDays(userId, filters);
      } catch (err: unknown) {
        if (cancelled || isBackground) {
          // A failed background refresh keeps the agenda already on screen.
          return;
        }

        const message =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Não foi possível carregar a agenda.";

        setErrorMessage(message);
        addToast({ title: message, type: "error" });
      } finally {
        if (!cancelled) {
          setLoadingInitial(false);
          setLoadingDay(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, canLoad, filters, revision, addToast]);

  // Week strip counts load on their own; the strip shows the dates meanwhile.
  useEffect(() => {
    if (!user || !canLoad) {
      return;
    }

    let cancelled = false;
    const baseKey = buildAgendaWeekCacheKey(Number(user.id), filters.teamId, getFortalezaTodayIso());
    const key = `${baseKey}|${revision}`;
    const cached = getCachedAgendaWeek(key);
    if (cached) {
      weekBaseKeyRef.current = baseKey;
      setWeekSummary(cached);
      return;
    }
    // Another team needs other counts; the same team keeps its counts while refreshing.
    if (weekBaseKeyRef.current !== baseKey) {
      setWeekSummary(null);
    }

    (async () => {
      try {
        await authorizeAgendaRequests(dateToExpires, access_token);
        const week = await fetchAgendaWeekSummary({ teamId: filters.teamId });
        setCachedAgendaWeek(key, week);
        if (!cancelled) {
          weekBaseKeyRef.current = baseKey;
          setWeekSummary(week);
        }
      } catch {
        // The strip keeps working with dates only; counts come with the next load.
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, canLoad, filters.teamId, revision]);

  async function prefetchAdjacentDays(userId: number, current: AgendaFiltersState) {
    const todayIso = getFortalezaTodayIso();
    for (const date of adjacentAgendaDates(current.date, todayIso)) {
      const neighbor = { ...current, date };
      const key = buildAgendaDayCacheKey(userId, neighbor);
      if (hasCachedAgendaDay(key)) {
        continue;
      }
      try {
        setCachedAgendaDay(key, await fetchAgendaDay(neighbor));
      } catch {
        // Prefetch is best effort.
      }
    }
  }

  return {
    filters,
    applyFilters,
    dayData,
    weekSummary,
    loadingInitial,
    loadingDay,
    errorMessage,
    retry,
    reloadToken,
  };
}
